/**
 * GpsGeocercas — administración de perímetros virtuales (mockup).
 *
 * Master-detail: lista de geocercas a la izquierda, mapa con TODAS las
 * geocercas + dispositivos a la derecha. Sin edición de geometría todavía;
 * el botón "Crear geocerca" queda como placeholder hasta el back.
 */
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Bell, BellOff, MapPin, Plus, Ruler, ShieldCheck } from 'lucide-react';
import useTheme from 'hooks/useTheme';
import { useIsMobile } from '../../../hooks/useIsMobile';
import { cn } from '../../../utils/cn';
import { Badge } from '../../../components/ui/Badge';
import { Button } from '../../../components/ui/Button';
import { Card } from '../../../components/ui/Card';
import { PageHeader } from '../../../components/ui/PageHeader';
import { Skeleton } from '../../../components/ui/Skeleton';
import FleetMap from './components/FleetMap';
import { gpsApi } from './gpsMock';

const reglaLabel = { entrada: 'Avisar al entrar', salida: 'Avisar al salir', ambos: 'Entrada y salida' };

export default function GpsGeocercas() {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const isMobile = useIsMobile(1024); // por debajo de lg: lista y mapa apilados
  const mapBoxRef = useRef(null);
  const [geocercas, setGeocercas] = useState(null);
  const [devices, setDevices] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [focusId, setFocusId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    // TODO(back): cablear cuando el back exponga /geocercas y /devices.
    Promise.all([gpsApi.geocercas(), gpsApi.devices()]).then(([g, d]) => {
      if (cancelled) return;
      setGeocercas(g);
      setDevices(d);
      if (g.length) setSelectedId(g[0].id);
    });
    return () => { cancelled = true; };
  }, []);

  // Master-detail: igual que GpsMonitoreo con los devices, seleccionar una
  // geocerca la marca en la lista (selectedId) y dispara el enfoque del mapa
  // (focusId → FleetMap/MapController vuela al perímetro).
  const handleSelect = (id) => {
    setSelectedId(id);
    setFocusId(id);
    // En mobile el mapa queda apilado debajo de la lista, fuera del viewport:
    // llevarlo a la vista para que el vuelo del focus sea visible.
    if (isMobile && mapBoxRef.current) {
      const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
      mapBoxRef.current.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'nearest' });
    }
  };

  return (
    <div className="w-full min-w-0 p-4 md:p-6 space-y-5">
      <PageHeader
        title={t("Geocercas")}
        subtitle={t("Define perímetros virtuales y dispara alertas cuando una unidad entra o sale.")}
        actions={<Button iconLeft={<Plus className="w-4 h-4" />}>{t("Crear geocerca")}</Button>}
      />

      <div className="grid grid-cols-1 lg:grid-cols-[380px_1fr] gap-4">
        {/* Lista */}
        <Card padding="none" className="overflow-hidden">
          <div className="px-4 py-3 border-b border-border flex items-center justify-between">
            <h2 className="text-sm font-semibold text-fg">{geocercas?.length ?? '…'} {t("geocercas")}</h2>
            <span className="text-[11px] text-fg-muted">{t("Activas + en pausa")}</span>
          </div>
          <ul className="divide-y divide-border max-h-[60vh] lg:max-h-[calc(100svh-280px)] overflow-y-auto">
            {geocercas === null
              ? Array.from({ length: 4 }).map((_, i) => (
                  // Espeja GeocercaRow: ícono cuadrado + nombre/badge + descripción + meta.
                  <li key={i} className="px-4 py-3 flex items-start gap-3">
                    <Skeleton className="w-7 h-7 rounded-md shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0 space-y-2">
                      <div className="flex items-center justify-between gap-2">
                        <Skeleton className="h-4 w-32" />
                        <Skeleton className="h-4 w-14 rounded-full" />
                      </div>
                      <Skeleton className="h-3 w-2/3" />
                      <Skeleton className="h-3 w-1/2" />
                    </div>
                  </li>
                ))
              : geocercas.map((g) => (
                  <GeocercaRow
                    key={g.id}
                    geocerca={g}
                    selected={selectedId === g.id}
                    onClick={() => handleSelect(g.id)}
                  />
                ))}
          </ul>
        </Card>

        {/* Mapa con todas las geocercas y dispositivos */}
        <div ref={mapBoxRef} className="rounded-xl border border-border bg-bg overflow-hidden shadow-card h-[60vh] lg:h-[calc(100svh-260px)] min-h-[360px] lg:min-h-[480px] relative">
          <FleetMap
            className="absolute inset-0"
            devices={devices}
            geocercas={geocercas ?? []}
            showGeocercas
            isDark={isDark}
            selectedId={selectedId}
            focusId={focusId}
            onSelect={handleSelect}
          />
        </div>
      </div>
    </div>
  );
}

function GeocercaRow({ geocerca, selected, onClick }) {
  const { t } = useTranslation();
  return (
    <li>
      <button
        onClick={onClick}
        className={cn(
          'w-full text-left px-4 py-3 flex items-start gap-3 transition-colors',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-500',
          selected ? 'bg-brand-50' : 'hover:bg-bg-muted',
        )}
      >
        <span
          className="w-7 h-7 rounded-md flex items-center justify-center shrink-0 mt-0.5"
          // geocerca.color es identidad por-registro (la misma que pinta el
          // perímetro en Leaflet); se usa como acento data-driven, no como token.
          style={{ background: `color-mix(in srgb, ${geocerca.color} 14%, transparent)`, color: geocerca.color }}
        >
          {geocerca.tipo === 'circular' ? <MapPin className="w-4 h-4" /> : <ShieldCheck className="w-4 h-4" />}
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            {/* En mobile no hay hover para leer el nombre completo: 2 líneas. */}
            <span
              className={cn('text-sm font-semibold line-clamp-2 lg:line-clamp-1', selected ? 'text-brand-700' : 'text-fg')}
              title={geocerca.nombre}
            >
              {geocerca.nombre}
            </span>
            {geocerca.alertaActiva ? (
              <Badge tone="success" dot>{t("Activa")}</Badge>
            ) : (
              <Badge tone="neutral"><BellOff className="w-3 h-3" /> {t("Pausa")}</Badge>
            )}
          </div>
          <p className="text-xs text-fg-muted mt-0.5 line-clamp-1">{geocerca.descripcion}</p>
          <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-fg-muted">
            <span className="inline-flex items-center gap-1">
              <Ruler className="w-3 h-3" />
              {geocerca.tipo === 'circular' && geocerca.radioM
                ? `${geocerca.radioM >= 1000 ? `${(geocerca.radioM / 1000).toFixed(1)} km` : `${geocerca.radioM} m`} ${t("radio")}`
                : t('Polígono')}
            </span>
            <span className="inline-flex items-center gap-1">
              <Bell className="w-3 h-3" />
              {t(reglaLabel[geocerca.regla])}
            </span>
            <span className="ml-auto tabular-nums">
              <span className="text-fg font-medium">{geocerca.dispositivosDentro}</span> /{' '}
              {geocerca.dispositivosAsignados} {t("dentro")}
            </span>
          </div>
        </div>
      </button>
    </li>
  );
}
